import React from "react";
import { Button } from "../../../components/ui";
import { formatCreatedOn } from "../../brand/planFormat";

/** How long a workspace's work is kept after the plan ends, as the card says
 *  it. The promise is the backend's to keep; this is only where it is made. */
export const GRACE_DAYS = 30;

interface RefundedPlanCardProps {
  /** What the plan was called — "Naurist Pro". */
  productName: string;
  /** The day the refund went back, and so the day access stopped. */
  endedOn: string | null;
  /** Takes them back through the checkout on the cycle they were on. */
  onReactivate: () => void;
}

/**
 * The plan, after the money went back.
 *
 * Grey and past tense: two pills that say what happened and one line that
 * dates it. There is nothing to administer here — no cycle, no renewal, no
 * "what's included" — so the card says less than the live one rather than the
 * same amount in a sadder colour.
 *
 * The way back is the only thing on it with any weight. Somebody reading this
 * screen a week later has usually come to undo it, and the answer they need
 * first is that their clients and plans are still there.
 */
const RefundedPlanCard: React.FC<RefundedPlanCardProps> = ({
  productName,
  endedOn,
  onReactivate,
}) => (
  <section
    aria-labelledby="ended-plan-heading"
    className="rounded-md border-[0.5px] border-[#E7E8EC] bg-[#EDEEF0]"
  >
    <div className="px-5 pt-4 pb-4 3xl:px-6 3xl:pt-5">
      <div className="flex flex-wrap items-center gap-2.5">
        <h2
          id="ended-plan-heading"
          className="text-[20px] 3xl:text-[24px] font-semibold text-ink-muted"
        >
          {productName}
        </h2>
        {/* What it is, then why. "Ended" is the state; "Refunded" is the
            reason, and the reason is the one that answers "where is my
            money". */}
        <span className="inline-flex whitespace-nowrap rounded-full bg-[#8B8D98] px-2 py-0.5 text-[12px] 3xl:text-[13px] font-medium text-white">
          Ended
        </span>
        <span className="inline-flex whitespace-nowrap rounded-full bg-[#FFEBD2] px-2 py-0.5 text-[12px] 3xl:text-[13px] font-medium text-[#BC7B2F]">
          Refunded
        </span>
      </div>

      <p className="mt-1.5 text-[13px] 3xl:text-[14px] text-ink-muted">
        {endedOn ? (
          <>
            Your subscription ended on{" "}
            {formatCreatedOn(new Date(endedOn))} after a full refund.
          </>
        ) : (
          <>Your subscription ended after a full refund.</>
        )}
      </p>
    </div>

    {/* White on grey, so the way back reads as something to act on rather
        than as one more line about a plan that is over. */}
    <div className="mx-5 mb-5 flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-white px-4 py-3.5 3xl:mx-6 3xl:mb-6">
      <div className="min-w-0">
        <p className="text-[14px] 3xl:text-[15px] font-medium text-ink">
          Changed your mind?
        </p>
        <p className="mt-0.5 text-[13px] 3xl:text-[14px] text-ink-muted">
          Reactivate {productName} anytime - your clients and plans are kept
          safe for {GRACE_DAYS} days.
        </p>
      </div>

      <Button variant="primary" size="sm" onClick={onReactivate}>
        Reactivate plan
      </Button>
    </div>
  </section>
);

export default RefundedPlanCard;
